import { useEffect, useRef } from "react"
import type { Editor } from "@tiptap/react"
import { graphqlClient } from "@/lib/graphql"
import { graphql } from "@/__generated__"

// Persist a plain-text snapshot of the shared notes so they survive the session
// (#42). The CRDT already keeps the two participants in sync; this just
// checkpoints the text server-side.
const SAVE_LESSON_NOTES = graphql(`
  mutation SaveLessonNotesFromClassroom($lessonId: ID!, $notes: String!) {
    saveLessonNotes(input: { lessonId: $lessonId, notes: $notes }) {
      lesson { id }
      errors
    }
  }
`)

export const NOTES_AUTOSAVE_DEBOUNCE_MS = 3000
// While someone keeps typing, save at least this often: it bounds what a crash,
// a dead battery or a browser killed in the background can lose (#1033).
export const NOTES_AUTOSAVE_MAX_WAIT_MS = 10_000
// Browsers allow ~64 KB of keepalive request bodies in flight per page and
// reject a request that doesn't fit. Stay under it, with room for the query.
const KEEPALIVE_BODY_LIMIT_BYTES = 60_000

function fitsKeepalive(lessonId: string, notes: string): boolean {
  const body = new TextEncoder().encode(JSON.stringify({ lessonId, notes }))
  return body.length < KEEPALIVE_BODY_LIMIT_BYTES - 2_000
}

// Debounced autosave of the plain-text notes to the lesson. Only inside a real
// session (enabled + lessonId); the marketing-demo fallback never saves. Both
// participants run this — the content is shared, so it's the same text and
// last-write-wins is harmless. Additive: it observes the editor, never touching
// the CRDT/transport layer.
//
// Every way a pending save could be lost goes through the same `flush`: the
// 3 s pause, the 10 s maximum wait, the editor going away (hang-up, Back,
// closing the Notes panel, a language switch rebuilding the editor) and the
// page going away (`pagehide`, `visibilitychange` to hidden).
export function useLessonNotesAutosave(editor: Editor | null, lessonId: string | undefined, enabled: boolean) {
  const lastSavedRef = useRef<string>("")

  useEffect(() => {
    if (!editor || !enabled || !lessonId) return

    let debounceTimer: ReturnType<typeof setTimeout> | null = null
    let maxWaitTimer: ReturnType<typeof setTimeout> | null = null
    // The latest text, kept by the update handler: by the time the cleanup runs
    // the editor may already have been rebuilt, so never ask it for text there.
    let pendingText = ""
    let waiting = false

    const clearTimers = () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      if (maxWaitTimer) clearTimeout(maxWaitTimer)
      debounceTimer = null
      maxWaitTimer = null
    }

    const send = (text: string, keepalive: boolean) =>
      graphqlClient.request(SAVE_LESSON_NOTES, { lessonId, notes: text }, keepalive ? { keepalive: true } : undefined)

    const flush = () => {
      clearTimers()
      if (!waiting) return
      waiting = false
      const text = pendingText
      if (text === lastSavedRef.current) return
      lastSavedRef.current = text
      const keepalive = fitsKeepalive(lessonId, text)
      send(text, keepalive).catch((e) => {
        if (keepalive) {
          // The browser may have refused the keepalive request (quota); try a
          // plain one, which finishes if the page is still alive.
          send(text, false).catch(() => {
            lastSavedRef.current = "" // allow a retry on the next edit
          })
          return
        }
        console.error("Failed to save lesson notes:", e)
        lastSavedRef.current = "" // allow a retry on the next edit
      })
    }

    const onUpdate = () => {
      pendingText = editor.getText()
      waiting = true
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(flush, NOTES_AUTOSAVE_DEBOUNCE_MS)
      if (!maxWaitTimer) maxWaitTimer = setTimeout(flush, NOTES_AUTOSAVE_MAX_WAIT_MS)
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flush()
    }

    editor.on("update", onUpdate)
    window.addEventListener("pagehide", flush)
    document.addEventListener("visibilitychange", onVisibilityChange)
    return () => {
      editor.off("update", onUpdate)
      window.removeEventListener("pagehide", flush)
      document.removeEventListener("visibilitychange", onVisibilityChange)
      flush()
    }
  }, [editor, enabled, lessonId])
}
